/**
 * padNotesHelpers.js
 *
 * Notes helpers for the CYP PAD Assessment Outcome page, whose clinician
 * boxes hold structured notes (a list of note objects) rather than a plain
 * string — the same treatment autism's equivalent boxes get.
 *
 * They live here rather than inline on the page because each one replaces a
 * string test that the page previously did in several places, and getting any
 * of them wrong is silent: an emptied box that still reads as filled unlocks
 * Next Steps it should not, and a mis-resolved prefill overwrites the
 * clinician's own goals with the MDT Plan copy.
 *
 * CYP-local on purpose. src/utils/mdtPlanUtils.js is shared with the adult
 * PAD and keeps its string-only contract.
 */

import { normaliseNotesValue } from '../../../utils/autismReviewData';
import { resolvePadGoalsClinicianNotes } from '../../../utils/mdtPlanUtils';

/**
 * Does this notes value carry any text?
 *
 * An array is always truthy, so the old `!value || value.trim() === ''` test
 * reports an emptied box as filled. Every gate that hangs off "has the
 * clinician written here" must ask this instead.
 *
 * @param {Array<object>|string|null|undefined} value
 * @returns {boolean}
 */
export function hasNoteText(value) {
  return normaliseNotesValue(value).some((note) => (note?.text || '').trim().length > 0);
}

/**
 * Compare two notes values by their content, so a re-render is not mistaken
 * for an edit (a fresh array with identical notes is a different reference).
 *
 * Text and source tags both count: re-tagging a note is a real change that
 * has to reach Firestore.
 *
 * @returns {boolean} true when the two values hold the same notes.
 */
export function sameNotes(a, b) {
  const signature = (value) => normaliseNotesValue(value)
    .map((note) => [
      (note?.text || '').trim(),
      (note?.criteriaTags || []).join(','),
    ].join('\u0000'))
    .join('\u0001');
  return signature(a) === signature(b);
}

/**
 * Resolve the Patient's Goals notes for the CYP PAD, deciding between what
 * the clinician has written on this page and the MDT Plan copy.
 *
 * The shared resolver only recognises a STRING as "the PAD field has been
 * written", because that is all the adult PAD ever stores. Structured notes
 * are an array, so without the array branch a clinician's own goals look
 * unwritten and get overwritten by the MDT Plan text on every mount — and
 * that write is persisted, so the loss is permanent.
 *
 * Written-and-then-emptied (`[]`) counts as written, exactly as `''` does on
 * the adult path: it means "the clinician deleted the prefill", not "nothing
 * has been recorded yet".
 *
 * @param {object} args
 * @param {Array<object>|string|null|undefined} args.padNotes - stored PAD goals notes.
 * @param {string} args.mdtPlanNotes - the MDT Plan's patient-goals text.
 * @returns {{ value: Array<object>, source: 'pad'|'mdtPlan'|null }}
 */
export function resolveCypPadGoalsNotes({ padNotes, mdtPlanNotes } = {}) {
  if (Array.isArray(padNotes)) {
    return { value: padNotes, source: 'pad' };
  }
  const resolved = resolvePadGoalsClinicianNotes({ padNotes, mdtPlanNotes });
  return { value: normaliseNotesValue(resolved.value), source: resolved.source };
}
